import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { CustomFieldsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { CustomFieldFilters, CustomFieldResource } from '@/types'
import { useTable } from '@/helpers/useTable'
import { dedupe } from '@/helpers/dedupe'
import { Button } from '../base/button'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'
import { titleize } from '@/helpers/formatText'
import { Badge } from '../base/badge'

interface Props extends TableProps {
  filters?: CustomFieldFilters
  createable?: boolean
}

const formatCustomFieldResource = (resourceType: CustomFieldResource) => {
  switch (resourceType) {
    case CustomFieldResource.AssetType: return 'Asset type'
    case CustomFieldResource.Asset: return 'Asset'
    case CustomFieldResource.Package: return 'Package'
    case CustomFieldResource.Venue: return 'Venue'
  }
}

export const Table = ({ pageSize = 20, filters = {}, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<CustomFieldsQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.customFields.pageInfo, onIndex, basePath: '/settings/custom-fields' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<CustomFieldFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Name' }, { name: 'Resource' }, { name: 'Type' }, { name: 'Enabled' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.customFields.nodes = dedupe([
              ...previousData.customFields.nodes,
              ...fetchMoreResult.customFields.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        rows={data.customFields.nodes.map(customField => (
          {
            id: customField.id,
            columns: [
              customField.name,
              formatCustomFieldResource(customField.resourceType),
              titleize(customField.fieldType),
              <Badge color={customField.enabled ? 'green' : 'red'}>
                { customField.enabled ? 'Yes' : 'No' }
              </Badge>
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
